import * as React from 'react';
import { Translate } from 'react-redux-i18n';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../reducers';
import { setActiveItem, toggleEditPanel, updateEditableLink, updateEditableMedia, resetActiveItem } from '../../../containers/Editor/actions';
import EditPanel from '../../../containers/Editor/EditPanel';
import MediaPanel from '../../../containers/Editor/EditPanel/MediaPanel';
import LinkPanel from '../../../containers/Editor/EditPanel/LinkPanel';
import MediaItem from '../../../containers/Editor/MediaEditor/MediaItem';
import VisibilityToggle from '../../../containers/Editor/VisibilityToggle';
import {
	Frame,
	FrameBorder,
	FrameLoader,
	FrameLoaderInner,
	FrameMask,
} from './styles';
import { Spinner } from '../../Spinner/styles';
import { EditorType, EditableType, IGNORE_PAGE_CLICK_CLASSNAME } from '../../../containers/Editor/constants';
import { getBorderWidth, PositionAndSize, isStyleIncludesHeight, LinkData } from '../../../containers/Editor/utils/helpers';
import Utils from '../../../utils';

interface MediaEditorProps {
	editorType: EditorType;
	type: EditableType;
	page: number;
	id: string;
	frameWidth: number;
	frameHeight: number;
	pageScale: number;
	isActive: boolean;
	canDragAndResize: boolean;
	dispatch: Dispatch<StoreState>;
	frameAspectRatio?: number;
	isDefaultMedia?: boolean;
	defaultStyle?: string;
	parentContainer?: HTMLElement | null;
	iframeContainer?: HTMLIFrameElement | null;
	isEditPanelDisplayed?: boolean;
	hasLink?: boolean;
	linkData?: LinkData;
	isProcessingVideo?: boolean;
	isCustom?: boolean;
	isVisible?: boolean;
	isInsideButton?: boolean;
	MediaComponent?: React.ComponentClass<any>; // tslint:disable-line no-any
	onLoad?: (width: number, height: number) => void;
	onFrameClick?: () => void;
	onFrameClickOutside?: () => void;
	onMediaClickOutside?: (positionAndSize: PositionAndSize) => void;
	isAccessibleVersion: boolean;
}

const MediaEditor = ({
	editorType = EditorType.INDESIGN,
	type,
	page,
	id,
	frameWidth,
	frameHeight,
	pageScale,
	frameAspectRatio,
	isDefaultMedia,
	defaultStyle,
	parentContainer,
	iframeContainer,
	isEditPanelDisplayed,
	isActive,
	canDragAndResize,
	hasLink,
	linkData,
	isProcessingVideo,
	dispatch,
	isCustom,
	isVisible = true,
	isInsideButton,
	MediaComponent = MediaItem,
	onLoad,
	onFrameClick,
	onFrameClickOutside,
	onMediaClickOutside,
	isAccessibleVersion,
}: MediaEditorProps) => {
	const [isFocusedInEditPanel, setIsFocusedInEditPanel] = React.useState(false);
	let isResizeListenerInitialized = React.useRef(false);

	const handleItemAfterLoad = () => {
		if (isActive && parentContainer) {
			parentContainer.classList.add('is-active');
		}
	};

	const handleClick = () => {
		if (!isActive && !isAccessibleVersion) {
			if (isCustom && onFrameClick) {
				return onFrameClick();
			}

			dispatch(setActiveItem(id, page, null));

			if (parentContainer) {
				parentContainer.classList.add('is-active');

				if (editorType === EditorType.HTML) {
					resetCustomMediaStyle();
				}
			}

			if (!isEditPanelDisplayed) {
				dispatch(toggleEditPanel());
			}
		}

		if (isAccessibleVersion && !isActive) {
			dispatch(resetActiveItem());
		}
	};

	const handleLinkChange = (linkData: LinkData) => {
		dispatch(updateEditableLink(page, id, {linkData}));
	};

	const handleVisibilityToggle = () => {
		dispatch(updateEditableMedia(page, id, {
			isVisible: !isVisible,
		}));
	};

	const renderEditPanel = () => {
		const editPanels = {
			[type]: <MediaPanel type={type} page={page} />,
		};

		if (hasLink) {
			editPanels[EditableType.link] = (
				<LinkPanel
					linkData={linkData}
					onChange={handleLinkChange}
					onFocus={() => setIsFocusedInEditPanel(true)}
					onBlur={() => setIsFocusedInEditPanel(false)}
				/>
			);
		}

		return (
			isActive && isEditPanelDisplayed && (
				<EditPanel
					defaultType={type}
					panels={editPanels}
				/>
			)
		);
	};

	const setCustomMediaStyle = () => {
		if (parentContainer) {
			const style = `position:relative;overflow:hidden;width:100%;height:${frameHeight}px;${defaultStyle}`;
			parentContainer.setAttribute('style', style);
		}
	};

	const resetCustomMediaStyle = () => {
		if (parentContainer) {
			if (defaultStyle) {
				parentContainer.setAttribute('style', defaultStyle);
			} else {
				parentContainer.removeAttribute('style');
			}
		}
	};

	React.useEffect(() => {
		if (editorType === EditorType.HTML) {
			return;
		}

		const handleWindowClick = (e: MouseEvent | TouchEvent) => {
			const mediaSelector = '.uprez-media-frame';
			const element = e.target as HTMLElement;
			const isMedia = !!element.closest(mediaSelector);

			if (!isMedia) {
				if (isCustom && onFrameClickOutside) {
					onFrameClickOutside();
				}

				if (parentContainer) {
					parentContainer.classList.remove('is-active');
				}
			}
		};

		window.addEventListener('mousedown', handleWindowClick);

		return () => {
			window.removeEventListener('mousedown', handleWindowClick);
		};
	}, []);

	React.useEffect(() => {
		if (editorType === EditorType.INDESIGN || !isActive) {
			return;
		}

		const handleWindowClick = (e: MouseEvent | TouchEvent) => {
			const mediaSelector = '.uprez-media-frame';
			const element = e.target as HTMLElement;
			const isMedia = !!element.closest(mediaSelector);

			if (!isMedia && !element.closest(`.${IGNORE_PAGE_CLICK_CLASSNAME}`)) {
				if (parentContainer) {
					parentContainer.classList.remove('is-active');

					if (isDefaultMedia) {
						resetCustomMediaStyle();
						if (type === EditableType.video) {
							parentContainer.classList.remove('is-video-cover');
						}
					} else {
						setCustomMediaStyle();
						if (type === EditableType.video) {
							parentContainer.classList.add('is-video-cover');
						}
					}
				}
			}
		};

		window.addEventListener('mousedown', handleWindowClick);

		if (iframeContainer) {
			iframeContainer.contentDocument!.addEventListener('mousedown', handleWindowClick);
		}

		return () => {
			window.removeEventListener('mousedown', handleWindowClick);

			if (iframeContainer) {
				iframeContainer.contentDocument!.removeEventListener('mousedown', handleWindowClick);
			}
		};
	}, [isActive, isDefaultMedia]);

	React.useEffect(() => {
		if (editorType !== EditorType.HTML || !parentContainer || isActive) {
			return;
		}

		// set styles on parentContainer when frame sizes are changed
		if (isDefaultMedia) {
			resetCustomMediaStyle();
		} else {
			setCustomMediaStyle();
		}
	}, [isActive, isDefaultMedia, frameWidth, frameHeight]);

	React.useEffect(() => {
		if (editorType !== EditorType.HTML || !parentContainer || type !== EditableType.video) {
			return;
		}

		if (isDefaultMedia) {
			parentContainer.classList.add('is-default-video');
			parentContainer.classList.remove('is-video-cover');
		} else {
			parentContainer.classList.add('is-video-cover');
			parentContainer.classList.remove('is-default-video');
		}
	}, [isDefaultMedia]);

	React.useEffect(() => {
		if (editorType !== EditorType.HTML || !iframeContainer || !parentContainer || isDefaultMedia) {
			return;
		}

		const handleResize = () => {
			const { width } = parentContainer.getBoundingClientRect();
			// this checking is needed to handle a case when parentContainer becomes invisible in viewport (width = 0)
			// (e.g., change an image in slider and click on next slide)
			if (width) {
				if (defaultStyle && isStyleIncludesHeight(defaultStyle)) {
					return;
				}

				parentContainer.style.height = `${width * (frameAspectRatio as number)}px`;
			}
		};

		if (!isResizeListenerInitialized.current && !isActive) {
			if (iframeContainer.contentWindow!.visualViewport) {
				iframeContainer.contentWindow!.visualViewport.addEventListener('resize', handleResize);
			} else {
				iframeContainer.contentWindow!.addEventListener('resize', handleResize);
			}
			isResizeListenerInitialized.current = true;
		}

		if (isResizeListenerInitialized.current && isActive) {
			if (iframeContainer.contentWindow!.visualViewport) {
				iframeContainer.contentWindow!.visualViewport.removeEventListener('resize', handleResize);
			} else {
				iframeContainer.contentWindow!.removeEventListener('resize', handleResize);
			}
			isResizeListenerInitialized.current = false;
		}
	}, [isDefaultMedia, isActive]);

	React.useEffect(() => {
		if (editorType === EditorType.INDESIGN && !isCustom && !isInsideButton && parentContainer) {
			parentContainer.style.overflow = 'visible';
		}
	}, []);

	const frameClasses = Utils.getCssClasses(`${IGNORE_PAGE_CLICK_CLASSNAME} uprez-media-frame`, {
		'is-active': isActive,
		'can-drag': canDragAndResize,
		'is-video-cover': type === EditableType.video,
	});
	const frameBorderClasses = Utils.getCssClasses('uprez-media-frame-border', {
		'is-active': isActive,
	});

	// is needed for rendering in iframe (for html editor)
	const frameStyle = {
		width: frameWidth ? `${frameWidth}px` : undefined,
		height: frameHeight ? `${frameHeight}px` : undefined,
	};

	if (editorType === EditorType.HTML && !isActive) {
		return (
			<MediaComponent
				type={type}
				page={page}
				id={id}
				scale={pageScale}
				isActive={isActive}
				isKeyActionsDisabled={!isActive || isFocusedInEditPanel}
				canDragAndResize={false}
				isCustom={isCustom}
				onAfterLoad={handleItemAfterLoad}
				onLoad={onLoad}
				onClick={handleClick}
				onClickOutside={onMediaClickOutside}
				parentContainer={parentContainer}
			/>
		);
	}

	return (
		<>
			{editorType === EditorType.INDESIGN && !isCustom && !isInsideButton &&
				<VisibilityToggle
					isVisible={isVisible}
					isParentActive={isActive}
					pageScale={pageScale}
					onToggle={handleVisibilityToggle}
				/>
			}
			<Frame
				className={frameClasses}
				isActive={isActive}
				canDragAndResize={canDragAndResize}
				mediaType={type}
				width={frameWidth}
				height={frameHeight}
				style={frameStyle}
				onClick={handleClick}
				onTouchEnd={handleClick}
				isAccessibleVersion={isAccessibleVersion}
			>
				{(isActive || !isVisible) &&
					<FrameBorder
						className={frameBorderClasses}
						isActive={isActive}
						borderWidth={getBorderWidth(pageScale)}
					/>
				}
				{isProcessingVideo &&
					<FrameLoader
						className="uprez-media-frame-loader"
						borderWidth={getBorderWidth(pageScale)}
					>
						<FrameMask className="uprez-media-frame-mask" />
						<FrameLoaderInner
							className="uprez-media-frame-loader-inner"
							scale={pageScale}
						>
							<Spinner
								className="uprez-media-frame-spinner"
								width="45px"
								height="45px"
							/>
							<Translate value="messages.video_processing" />
						</FrameLoaderInner>
					</FrameLoader>
				}
				{isVisible &&
					<MediaComponent
						type={type}
						page={page}
						id={id}
						scale={pageScale}
						isActive={isActive}
						isKeyActionsDisabled={!isActive || isFocusedInEditPanel}
						canDragAndResize={canDragAndResize}
						isCustom={isCustom}
						onAfterLoad={handleItemAfterLoad}
						onLoad={onLoad}
						onClickOutside={onMediaClickOutside}
						iframeContainer={iframeContainer}
					/>
				}
				{renderEditPanel()}
			</Frame>
		</>
	);
};

export default MediaEditor;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/MediaEditor/index.tsx